import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Spin,
    App,
    Alert,
    Button,
    Space,
    Input,
    Switch
} from 'antd';
import {
    UserOutlined,
    QrcodeOutlined,
    LoginOutlined,
    ShopOutlined
} from '@ant-design/icons';
import { deviceAuthService, deviceCashierService } from '../../services/posApi';
import { usePageTitle } from '../../context/PageTitleContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

// Функция для декодирования JWT токена
const decodeJWT = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Ошибка декодирования JWT:', error);
        return null;
    }
};

const PosCashierLoginPage = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const { message } = App.useApp();
    const navigate = useNavigate();

    const [cashierUuid, setCashierUuid] = useState('');
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);
    const [error, setError] = useState(null);
    const [barcodeBuffer, setBarcodeBuffer] = useState('');
    const [lastKeystroke, setLastKeystroke] = useState(0);
    const [selfServiceMode, setSelfServiceMode] = useState(false);


    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Авторизация кассира');
        setRightContent(null);

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Проверяем авторизацию при загрузке
    useEffect(() => {
        checkAuthStatus();
    }, []);

    const isElectron = Boolean(window?.process?.versions?.electron);

    useEffect(() => {
        if (!isElectron) return;

        const handleKeyDown = (e) => {
            const currentTime = Date.now();
            const timeDiff = currentTime - lastKeystroke;

            if (timeDiff > 200) setBarcodeBuffer('');
            setLastKeystroke(currentTime);

            if (e.key === 'Enter') {
                if (barcodeBuffer.length >= 4) {
                    setCashierUuid(barcodeBuffer);
                    handleLogin(barcodeBuffer);
                }
                setBarcodeBuffer('');
                e.preventDefault();
            } else if (e.key.length === 1 && /[0-9a-zA-Z\-]/.test(e.key)) {
                setBarcodeBuffer(prev => prev + e.key);
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [barcodeBuffer, lastKeystroke, isElectron]);


    const checkAuthStatus = () => {
        setChecking(true);

        // Проверяем, авторизовано ли устройство
        if (!deviceAuthService.isAuthenticated()) {
            window.location.href = '/device/login';
            return;
        }

        // Проверяем JWT токен на наличие cashier_id
        const accessToken = localStorage.getItem('device_access_token');
        if (accessToken) {
            const decodedToken = decodeJWT(accessToken);
            if (decodedToken && decodedToken.cashier_id) {
                // Кассир уже авторизован в JWT, редиректим в обычный режим
                setTimeout(() => {
                    navigate('/device/sale');
                }, 100);
                return;
            }
        }

        // Проверяем локальное хранилище cashier info
        const savedCashierInfo = deviceCashierService.getSavedCashierInfo();
        if (savedCashierInfo && savedCashierInfo.cashier_authorized === true) {
            // Кассир авторизован в локальном хранилище, редиректим в обычный режим
            navigate('/device/sale');
            return;
        }

        // Кассир не авторизован, показываем страницу
        setChecking(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    const handleLogin = async (scannedUuid = null) => {
        const uuidToUse = scannedUuid || cashierUuid.trim();

        if (!uuidToUse) {
            message.error('Введите UUID кассира');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Генерируем UUID для корреляции и идемпотентности
            const generateUUID = () => {
                return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                    const r = Math.random() * 16 | 0;
                    const v = c === 'x' ? r : (r & 0x3 | 0x8);
                    return v.toString(16);
                });
            };

            const correlationKey = generateUUID();
            const idempotencyKey = generateUUID();

            const response = await deviceCashierService.loginCashier(
                uuidToUse,
                correlationKey,
                idempotencyKey
            );

            // Успешная авторизация
            message.success(`Добро пожаловать, ${response.cashier.f_name} ${response.cashier.l_name}!`);

            // Сохраняем информацию о кассире
            localStorage.setItem('device_cashier_info', JSON.stringify({
                cashier_authorized: true,
                cashier: response.cashier
            }));

            window.dispatchEvent(new CustomEvent('cashierStatusUpdate'));

            // Редиректим в зависимости от режима
            setTimeout(() => {
                if (selfServiceMode) {
                    navigate('/device/self-service');
                } else {
                    navigate('/device/sale');
                }
            }, 100);

        } catch (error) {
            setError(error.message);
            message.error(`Ошибка авторизации: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Если проверяем статус авторизации
    if (checking) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh'
            }}>
                <Space direction="vertical" align="center" size="large">
                    <Spin size="large" />
                    <Text>Проверка авторизации...</Text>
                </Space>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                padding: '24px'
            }}
        >
            <Card
                variant="bordered"
                style={{
                    width: '100%',
                    maxWidth: 500,
                    textAlign: 'center'
                }}
            >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* Иконка QR кода */}
                    <div>
                        <QrcodeOutlined
                            style={{
                                fontSize: '4rem',
                                color: '#05D32A',
                                marginBottom: '16px'
                            }}
                        />
                    </div>

                    {/* Заголовок */}
                    <div>
                        <Title
                            level={2}
                            style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                margin: 0,
                                marginBottom: '8px'
                            }}
                        >
                            Отсканируйте QR кассира
                        </Title>
                    </div>

                    {!isElectron && (
                        <div style={{ width: '100%' }}>
                            <Input.Group compact style={{ display: 'flex' }}>
                                <Input
                                    size="large"
                                    placeholder="UUID кассира"
                                    value={cashierUuid}
                                    onChange={(e) => setCashierUuid(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    prefix={<UserOutlined />}
                                    style={{ flex: 1 }}
                                    disabled={loading}
                                />
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<LoginOutlined />}
                                    onClick={handleLogin}
                                    loading={loading}
                                    disabled={!cashierUuid.trim()}
                                >
                                    Войти
                                </Button>
                            </Input.Group>
                        </div>
                    )}

                    {/* Ошибка */}
                    {error && (
                        <Alert
                            message="Ошибка авторизации"
                            description={error}
                            type="error"
                            showIcon
                            style={{ textAlign: 'left' }}
                        />
                    )}

                    {/* Self-Service Mode Switch */}
                    <div style={{
                        marginTop: 16,
                        padding: '16px',
                        border: selfServiceMode ? '2px solid #05D32A' : '1px solid #d9d9d9',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: selfServiceMode ? 'rgba(5, 211, 42, 0.05)' : 'transparent',
                        transition: 'all 0.3s'
                    }}>
                        <Space>
                            <ShopOutlined style={{ fontSize: '20px', color: selfServiceMode ? '#05D32A' : '#8c8c8c' }} />
                            <div style={{ textAlign: 'left' }}>
                                <div style={{
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    color: selfServiceMode ? '#05D32A' : 'inherit'
                                }}>
                                    Режим КСО
                                </div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    Самообслуживание
                                </div>
                            </div>
                        </Space>
                        <Switch
                            checked={selfServiceMode}
                            onChange={(checked) => setSelfServiceMode(checked)}
                            size="default"
                        />
                    </div>
                </Space>
            </Card>
        </div>
    );
};

export default PosCashierLoginPage;